import React, { useState, useEffect, useCallback } from 'react';
import { notify, confirmDialog } from '../ui/notify';
import { authHeaders } from '../config';

/**
 * Station Build Guide Component
 * Ermöglicht die Erstellung und Bearbeitung von Schritt-für-Schritt Aufbauanleitungen
 * mit Foto-Upload und generellen Hinweisen pro Station
 */
const StationBuildGuide = ({ 
  stationId, 
  onSave = null,
  onDataChange = null, // Neuer Callback für Datenänderungen
  readOnly = false 
}) => {
  const [buildSteps, setBuildSteps] = useState([]);
  const [generalNotes, setGeneralNotes] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editingStepId, setEditingStepId] = useState(null);
  const [draggedStepId, setDraggedStepId] = useState(null);
  const [hoveredImageUrl, setHoveredImageUrl] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  // Build Steps laden
  useEffect(() => {
    const loadBuildGuide = async () => {
      if (!stationId) return;
      
      try {
        const response = await fetch(`/api/station-layouts/${stationId}/build-guide`);
        if (response.ok) {
          const data = await response.json();
          setBuildSteps(data.steps || []);
          setGeneralNotes(data.generalNotes || '');
          console.log(`📋 Build Guide loaded for station ${stationId}:`, data);
        } else {
          // Neue Station - leere Anleitung
          setBuildSteps([]);
          setGeneralNotes('');
        }
      } catch (error) {
        console.error('Fehler beim Laden der Aufbauanleitung:', error);
        setBuildSteps([]);
        setGeneralNotes('');
      }
    };

    loadBuildGuide();
  }, [stationId]);

  // Änderungen an Parent weiterleiten
  const notifyDataChange = useCallback(() => {
    if (onDataChange) {
      const guideData = {
        steps: buildSteps,
        generalNotes,
        stationId
      };
      onDataChange(guideData);
    }
  }, [buildSteps, generalNotes, stationId, onDataChange]);

  // Bei Änderungen der Daten Parent benachrichtigen
  useEffect(() => {
    notifyDataChange();
  }, [buildSteps, generalNotes, notifyDataChange]);

  // Speichern
  const saveBuildGuide = useCallback(async () => {
    if (!stationId) return;
    
    setIsSaving(true);
    try {
      const guideData = {
        steps: buildSteps,
        generalNotes
      };

      if (onSave) {
        await onSave(stationId, guideData);
      } else {
        const response = await fetch(`/api/station-layouts/${stationId}/build-guide`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(guideData)
        });

        if (!response.ok) {
          throw new Error('Fehler beim Speichern der Aufbauanleitung');
        }
      }

      setLastSaved(new Date());
      console.log(`💾 Build Guide saved for station ${stationId}`);
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      notify('Fehler beim Speichern der Aufbauanleitung');
    } finally {
      setIsSaving(false);
    }
  }, [stationId, buildSteps, generalNotes, onSave]);

  // Neuen Schritt hinzufügen
  const addBuildStep = () => {
    const newStep = {
      id: Date.now(),
      title: 'Neuer Schritt',
      description: '',
      imageUrl: '',
      order: buildSteps.length + 1,
      estimatedTime: '',
      materials: [],
      isCompleted: false
    };
    setBuildSteps([...buildSteps, newStep]);
    setEditingStepId(newStep.id);
  };

  // Schritt bearbeiten
  const updateBuildStep = (stepId, updates) => {
    setBuildSteps(prev => prev.map(step => 
      step.id === stepId ? { ...step, ...updates } : step
    ));
  };

  // Schritt löschen
  const deleteBuildStep = async (stepId) => {
    if (await confirmDialog('Schritt wirklich löschen?')) {
      setBuildSteps(prev => {
        const filtered = prev.filter(step => step.id !== stepId);
        // Neu nummerieren
        return filtered.map((step, index) => ({ ...step, order: index + 1 }));
      });
    }
  };

  // Drag & Drop für Reihenfolge
  const handleDragStart = (e, stepId) => {
    setDraggedStepId(stepId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetStepId) => {
    e.preventDefault();
    
    if (draggedStepId === targetStepId) return;

    const draggedIndex = buildSteps.findIndex(step => step.id === draggedStepId);
    const targetIndex = buildSteps.findIndex(step => step.id === targetStepId);
    
    const newSteps = [...buildSteps];
    const [draggedStep] = newSteps.splice(draggedIndex, 1);
    newSteps.splice(targetIndex, 0, draggedStep);
    
    // Neu nummerieren
    const reorderedSteps = newSteps.map((step, index) => ({ ...step, order: index + 1 }));
    setBuildSteps(reorderedSteps);
    setDraggedStepId(null);
  };

  // Foto Upload Handler
  const handleImageUpload = async (stepId, file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);
    formData.append('stepId', stepId);

    try {
      const response = await fetch(`/api/upload/build-guide-image/${stationId}`, {
        method: 'POST',
        body: formData,
        headers: { ...authHeaders() },
      });

      if (response.ok) {
        const data = await response.json();
        updateBuildStep(stepId, { imageUrl: data.imageUrl });
        console.log('📷 Image uploaded:', data.imageUrl);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Upload fehlgeschlagen');
      }
    } catch (error) {
      console.error('Fehler beim Upload:', error);
      notify('Fehler beim Hochladen des Bildes: ' + error.message);
    }
  };

  // Mouse Position für Tooltip
  const handleMouseMove = (e) => {
    setMousePosition({ x: e.clientX, y: e.clientY });
  };

  // Auto-Save alle 30 Sekunden
  useEffect(() => {
    const autoSaveInterval = setInterval(() => {
      if (buildSteps.length > 0 || generalNotes.trim()) {
        saveBuildGuide();
      }
    }, 30000);

    return () => clearInterval(autoSaveInterval);
  }, [saveBuildGuide, buildSteps, generalNotes]);

  return (
    <div className="card h-100">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h6 className="mb-0">
          <i className="fas fa-hammer me-2"></i>
          Aufbauanleitung - Station {stationId}
        </h6>
        <div className="d-flex align-items-center gap-2">
          {lastSaved && (
            <small className="text-muted">
              Gespeichert: {lastSaved.toLocaleTimeString()}
            </small>
          )}
          {isSaving && (
            <div className="text-primary">
              <i className="fas fa-spinner fa-spin"></i>
            </div>
          )}
          {!readOnly && (
            <div className="btn-group">
              <button
                className={`btn btn-sm ${isEditing ? 'btn-success' : 'btn-outline-primary'}`}
                onClick={() => setIsEditing(!isEditing)}
              >
                <i className={`fas ${isEditing ? 'fa-check' : 'fa-edit'} me-1`}></i>
                {isEditing ? 'Fertig' : 'Bearbeiten'}
              </button>
              <button
                className="btn btn-sm btn-primary"
                onClick={saveBuildGuide}
                disabled={isSaving}
              >
                <i className="fas fa-save me-1"></i>
                Speichern
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="card-body" style={{ maxHeight: '800px', overflowY: 'auto' }}>
        {/* Build Steps */}
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6>📋 Aufbau-Schritte</h6>
            {isEditing && (
              <button
                className="btn btn-sm btn-success"
                onClick={addBuildStep}
              >
                <i className="fas fa-plus me-1"></i>
                Schritt hinzufügen
              </button>
            )}
          </div>

          {buildSteps.length === 0 ? (
            <div className="text-center py-4 text-muted">
              <i className="fas fa-clipboard-list fa-3x mb-3"></i>
              <p>Noch keine Aufbau-Schritte definiert</p>
              {isEditing && (
                <button className="btn btn-primary" onClick={addBuildStep}>
                  Ersten Schritt hinzufügen
                </button>
              )}
            </div>
          ) : (
            <div className="list-group">
              {buildSteps
                .sort((a, b) => a.order - b.order)
                .map((step, index) => (
                  <div
                    key={step.id}
                    className={`list-group-item ${draggedStepId === step.id ? 'opacity-50' : ''}`}
                    draggable={isEditing}
                    onDragStart={(e) => handleDragStart(e, step.id)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, step.id)}
                  >
                    <div className="d-flex align-items-start">
                      {/* Schritt Nummer */}
                      <div className="me-3">
                        <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" 
                             style={{ width: '32px', height: '32px', fontSize: '14px', fontWeight: 'bold' }}>
                          {step.order}
                        </div>
                      </div>

                      {/* Content */}
                      <div className="flex-grow-1">
                        {editingStepId === step.id ? (
                          // Edit Mode
                          <div>
                            <input
                              type="text"
                              className="form-control form-control-sm mb-2"
                              value={step.title}
                              onChange={(e) => updateBuildStep(step.id, { title: e.target.value })}
                              placeholder="Schritt-Titel"
                            />
                            <textarea
                              className="form-control form-control-sm mb-2"
                              rows="3"
                              value={step.description}
                              onChange={(e) => updateBuildStep(step.id, { description: e.target.value })}
                              placeholder="Beschreibung des Schritts..."
                            />
                            <div className="row mb-2">
                              <div className="col-md-6">
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  value={step.estimatedTime}
                                  onChange={(e) => updateBuildStep(step.id, { estimatedTime: e.target.value })}
                                  placeholder="Geschätzte Zeit (z.B. 15 min)"
                                />
                              </div>
                              <div className="col-md-6">
                                <input
                                  type="file"
                                  className="form-control form-control-sm"
                                  accept="image/*"
                                  onChange={(e) => handleImageUpload(step.id, e.target.files[0])}
                                />
                              </div>
                            </div>
                            <div className="d-flex gap-2">
                              <button
                                className="btn btn-sm btn-success"
                                onClick={() => setEditingStepId(null)}
                              >
                                <i className="fas fa-check"></i>
                              </button>
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => deleteBuildStep(step.id)}
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            </div>
                          </div>
                        ) : (
                          // View Mode
                          <div>
                            <div className="d-flex justify-content-between align-items-start">
                              <h6 className="mb-1">{step.title}</h6>
                              <div className="d-flex align-items-center gap-2">
                                {step.estimatedTime && (
                                  <small className="text-muted">
                                    <i className="fas fa-clock me-1"></i>
                                    {step.estimatedTime}
                                  </small>
                                )}
                                {step.imageUrl && (
                                  <span
                                    className="text-primary"
                                    style={{ cursor: 'pointer' }}
                                    onMouseEnter={(e) => {
                                      setHoveredImageUrl(step.imageUrl);
                                      handleMouseMove(e);
                                    }}
                                    onMouseLeave={() => setHoveredImageUrl(null)}
                                    onMouseMove={handleMouseMove}
                                  >
                                    <i className="fas fa-image"></i>
                                  </span>
                                )}
                                {isEditing && (
                                  <button
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => setEditingStepId(step.id)}
                                  >
                                    <i className="fas fa-edit"></i>
                                  </button>
                                )}
                              </div>
                            </div>
                            {step.description && (
                              <p className="mb-0 text-muted" style={{ fontSize: '0.9rem' }}>
                                {step.description}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>

        {/* Generelle Hinweise */}
        <div>
          <h6 className="mb-3">
            <i className="fas fa-lightbulb me-2"></i>
            Generelle Hinweise
          </h6>
          {isEditing ? (
            <textarea
              className="form-control"
              rows="6"
              value={generalNotes}
              onChange={(e) => setGeneralNotes(e.target.value)}
              placeholder="Allgemeine Hinweise zum Aufbau dieser Station...&#10;&#10;Beispiele:&#10;- Sicherheitshinweise&#10;- Besondere Materialien&#10;- Wichtige Reihenfolgen&#10;- Kontakte für Rückfragen"
            />
          ) : (
            <div className="p-3 bg-light rounded">
              {generalNotes ? (
                <pre style={{ whiteSpace: 'pre-wrap', margin: 0, fontSize: '0.9rem' }}>
                  {generalNotes}
                </pre>
              ) : (
                <em className="text-muted">Keine generellen Hinweise hinterlegt</em>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Image Tooltip */}
      {hoveredImageUrl && (
        <div
          className="position-fixed bg-white border shadow-lg rounded p-2"
          style={{
            left: mousePosition.x + 10,
            top: mousePosition.y - 100,
            zIndex: 1000,
            maxWidth: '300px'
          }}
        >
          <img
            src={hoveredImageUrl}
            alt="Schritt-Bild"
            style={{ maxWidth: '100%', height: 'auto', borderRadius: '4px' }}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'block';
            }}
          />
          <div style={{ display: 'none' }} className="text-muted text-center">
            <i className="fas fa-exclamation-triangle"></i> Bild nicht verfügbar
          </div>
        </div>
      )}
    </div>
  );
};

export default StationBuildGuide;